import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { sendOrderMessage, getOrderMessagesByOrderId } from '../lib/messagesService';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = { fontFamily: 'Sora, sans-serif', fontWeight: 700 };
const bodyFont = { fontFamily: 'Montserrat, sans-serif', fontWeight: 500 };

// Helper functions to handle trailing spaces and tab characters in field names
const getUserId = (orderData) => {
  if (orderData?.userId) return orderData.userId;
  if (orderData?.['userId ']) return orderData['userId '];
  if (orderData?.['userId\t']) return orderData['userId\t'];
  return undefined;
};

const getSellerId = (orderData) => {
  if (orderData?.sellerId) return orderData.sellerId;
  if (orderData?.['sellerId ']) return orderData['sellerId '];
  if (orderData?.['sellerId\t']) return orderData['sellerId\t'];
  return undefined;
};

export function CustomerOrderChat({ orderId, isOpen, onClose, order }) {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  // Handle case where userId might have trailing space or tab in key name
  const userId = getUserId(order) || order?.['userId '] || order?.['userId\t'] || order?.userId;
  const sellerId = getSellerId(order) || order?.['sellerId '] || order?.['sellerId\t'] || order?.sellerId;

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch order details
  useEffect(() => {
    if (!orderId || !isOpen) return;

    // If we have order data as a prop, use it first
    if (order && Object.keys(order).length > 0) {
      const cleanedOrder = {
        ...order,
        userId: getUserId(order),
        sellerId: getSellerId(order)
      };
      setOrderDetails(cleanedOrder);
      return;
    }

    const fetchOrderDetails = async () => {
      try {
        const orderRef = doc(db, 'orders', orderId);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const orderData = orderSnap.data();
          setOrderDetails(orderData);
        } else {
          console.error('Order not found:', orderId);
          setError('Order not found');
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details');
      }
    };

    fetchOrderDetails();
  }, [orderId, isOpen, order]);

  // Subscribe to messages for this order
  useEffect(() => {
    if (!orderId || !isOpen || !orderDetails) return;

    setLoading(true);
    setError('');

    // Verify user is the customer for this order
    const isCustomer = currentUser?.uid === orderDetails?.userId;
    
    if (!isCustomer) {
      setError('You do not have permission to view messages for this order');
      setLoading(false);
      return;
    }

    const messagesQuery = query(
      collection(db, 'orderMessages'),
      where('orderId', '==', orderId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messageList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      
      // Filter messages to ensure user only sees messages they should have access to
      const filteredMessages = messageList.filter(message => 
        message.senderId === currentUser?.uid || 
        message.receiverId === currentUser?.uid
      );
      
      setMessages(filteredMessages);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to messages:', err);
      setError('Failed to load messages');
      setLoading(false);
    });

    return unsubscribe;
  }, [orderId, isOpen, orderDetails, currentUser?.uid]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || sending || !orderDetails) return;

    setSending(true);
    
    try {
      const messageData = {
        orderId: orderId,
        senderId: currentUser?.uid,
        receiverId: orderDetails.sellerId,
        senderName: currentUser?.displayName || 'Customer',
        senderType: 'customer',
        message: newMessage.trim()
      };

      await sendOrderMessage(messageData);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : timestamp.toDate();
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get order status label
  const getStatusLabel = (status) => {
    const statusLabels = {
      processing: 'Processing',
      'out for delivery': 'Out for Delivery',
      delivered: 'Delivered',
      cancelled: 'Cancelled',
      confirmed: 'Confirmed',
      paid: 'Paid'
    };
    return statusLabels[status] || status;
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: NEUTRAL_WHITE,
        borderRadius: 18,
        width: '100%',
        maxWidth: 480,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #F0F0F0',
          background: NEUTRAL_WHITE
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12
          }}>
            <h2 style={{
              ...headingFont,
              fontSize: 16,
              color: NEUTRAL_TEXT,
              margin: 0
            }}>
              Message Seller
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 24,
                cursor: 'pointer',
                color: ACCENT_GRAY,
                padding: 0,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
          
          {/* Order Context */}
          {orderDetails && (
            <div style={{
              background: NEUTRAL_BG,
              borderRadius: 10,
              padding: 12,
              marginBottom: 8
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 4
              }}>
                <span style={{
                  fontSize: 12,
                  color: NEUTRAL_TEXT,
                  fontWeight: 600,
                  ...bodyFont
                }}>
                  Order #{orderId}
                </span>
                <span style={{
                  background: PRIMARY_BROWN,
                  color: NEUTRAL_WHITE,
                  borderRadius: 8,
                  padding: '4px 8px',
                  fontSize: 10,
                  fontWeight: 600,
                  ...bodyFont
                }}>
                  {getStatusLabel(orderDetails.status)}
                </span>
              </div>
              <div style={{
                fontSize: 12,
                color: ACCENT_GRAY,
                ...bodyFont
              }}>
                {orderDetails.items?.length || 0} item{orderDetails.items?.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          background: NEUTRAL_BG
        }}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              color: ACCENT_GRAY,
              padding: 40,
              fontSize: 12
            }}>
              Loading messages...
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              color: ERROR_RED,
              padding: 40,
              fontSize: 12
            }}>
              {error}
            </div>
          ) : messages.length === 0 ? (
            <div style={{
              textAlign: 'center',
              color: ACCENT_GRAY,
              padding: 40,
              fontSize: 12
            }}>
              No messages yet. Start the conversation!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {messages.map((message) => {
                const isCustomer = message.senderId === currentUser?.uid;
                return (
                  <div
                    key={message.id}
                    style={{
                      display: 'flex',
                      justifyContent: isCustomer ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <div style={{
                      maxWidth: '70%',
                      background: isCustomer ? PRIMARY_BROWN : NEUTRAL_WHITE,
                      color: isCustomer ? NEUTRAL_WHITE : NEUTRAL_TEXT,
                      borderRadius: 16,
                      padding: '12px 16px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{
                        fontSize: 12,
                        fontWeight: 500,
                        marginBottom: 4,
                        ...bodyFont
                      }}>
                        {message.message}
                      </div>
                      <div style={{
                        fontSize: 10,
                        color: isCustomer ? 'rgba(255,255,255,0.7)' : ACCENT_GRAY,
                        textAlign: 'right'
                      }}>
                        {formatTime(message.timestamp)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Message Input */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid #F0F0F0',
          background: NEUTRAL_WHITE
        }}>
          <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Write a message to the seller..."
              disabled={sending}
              style={{
                flex: 1,
                border: '1px solid #E0E0E0',
                borderRadius: 20,
                padding: '12px 16px',
                fontSize: 12,
                outline: 'none',
                ...bodyFont
              }}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              style={{
                background: newMessage.trim() && !sending ? PRIMARY_BROWN : '#E0E0E0',
                color: NEUTRAL_WHITE,
                border: 'none',
                borderRadius: 20,
                padding: '12px 20px',
                fontSize: 12,
                fontWeight: 600,
                cursor: newMessage.trim() && !sending ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s ease',
                ...bodyFont
              }}
            >
              {sending ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
